import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  retainingWallsBuildUpApi,
  RetainingWallsBuildUpRequest,
} from "../api/retainingWallsBuildUp";

export const RETAINING_WALLS_BUILD_UP_QUERY_KEYS = {
  detail: (tenderName: string, tenderVersion: number) =>
    ["retainingWallsBuildUp", tenderName, tenderVersion] as const,
};

export const useGetRetainingWallsBuildUp = (
  tenderName: string,
  tenderVersion: number,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnMount?: boolean | "always";
    refetchOnWindowFocus?: boolean;
  }
) => {
  const effectiveVersion = tenderVersion === 0 ? 1 : tenderVersion;
  return useQuery({
    queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(tenderName, effectiveVersion),
    queryFn: () => retainingWallsBuildUpApi.get(tenderName, effectiveVersion),
    enabled: (options?.enabled !== false) && !!tenderName && effectiveVersion >= 1,
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    refetchOnMount: options?.refetchOnMount,
    // Callers use `staleTime: 0` + `refetchOnMount: "always"` to always pick
    // up a save made via a direct API POST (which bypasses React Query's own
    // invalidation) — that's a "refetch on mount" need, not a "refetch on
    // window focus" one. Defaulting this to `false` (no call site opts in
    // today) stops every mounted instance of this query — Sum Analysis, the
    // Ret & Screen Walls Bill Summary, Plot Matrix, GroundworksReview's own
    // totals hook — from refetching just because the browser tab regained
    // focus, which was the cause of /groundworks/review's "reloads on every
    // tab switch" symptom.
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });
};

export const usePostRetainingWallsBuildUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RetainingWallsBuildUpRequest) =>
      retainingWallsBuildUpApi.post(data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(variables.tenderName, variables.tenderVersion),
      });
    },
    onError: (error: any) => {
      console.error("❌ Error saving retaining walls build up:", error);
    },
  });
};
